import { useState, type FormEvent } from "react";

import { GordonLandApiError, loginWithNickname, type SessionUser } from "./gordonland-api.js";

interface AuthScreenProps {
  readonly onAuthenticated: (user: SessionUser) => void;
}

function authMessage(error: unknown): string {
  if (!(error instanceof GordonLandApiError)) return "暂时无法连接 GordonLand，请检查网络后重试。";
  if (error.code === "RATE_LIMITED" && error.retryAfterSeconds !== undefined) {
    const hours = Math.ceil(error.retryAfterSeconds / 3600);
    return `登录已被安全保护，请约 ${hours} 小时后重试。`;
  }
  if (error.code === "INVALID_CREDENTIALS" && error.attemptsRemaining !== undefined) {
    return `昵称或密码不正确，还可尝试 ${error.attemptsRemaining} 次。`;
  }
  return error.message;
}

export function AuthScreen({ onAuthenticated }: AuthScreenProps) {
  const [nickname, setNickname] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setMessage("");
    try {
      const result = await loginWithNickname(nickname, password);
      onAuthenticated(result.user);
    } catch (error: unknown) {
      setMessage(authMessage(error));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="auth-shell">
      <div className="auth-island" aria-hidden="true">
        <i /><i /><i /><i />
      </div>
      <section className="auth-card" aria-labelledby="auth-title">
        <div className="auth-brand"><span>G</span><strong>GordonLand</strong></div>
        <p className="auth-eyebrow">属于朋友们的私密岛屿</p>
        <h1 id="auth-title">回到你的世界</h1>
        <p className="auth-intro">使用昵称和密码登录。首次使用的新昵称会自动创建账号。</p>
        <form onSubmit={(event) => { void submit(event); }}>
          <label>
            <span>用户名（昵称）</span>
            <input
              autoComplete="username"
              maxLength={24}
              minLength={2}
              name="nickname"
              onChange={(event) => setNickname(event.currentTarget.value)}
              required
              value={nickname}
            />
          </label>
          <label>
            <span>密码</span>
            <input
              autoComplete="current-password"
              maxLength={128}
              minLength={8}
              name="password"
              onChange={(event) => setPassword(event.currentTarget.value)}
              required
              type="password"
              value={password}
            />
          </label>
          {message !== "" ? <p className="auth-error" role="alert">{message}</p> : null}
          <button className="auth-submit" disabled={submitting} type="submit">
            {submitting ? "正在安全登录…" : "进入 GordonLand"}
          </button>
        </form>
        <div className="auth-safety">
          <strong>安全保护已开启</strong>
          <span>连续 5 次密码错误后，设备将暂停登录 12 小时。</span>
        </div>
      </section>
    </main>
  );
}
